import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Instagram, Facebook, Phone, ExternalLink } from "lucide-react";
// CategoryNav removed
// InstagramWidget removed — only on home page
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const DECORATOR_TYPE_OPTIONS = ["Decorators", "Entertainment", "DJs", "Rentals", "Valet Services", "Helpers"];

const DECORATOR_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "city", label: "City", placeholder: "Frisco" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Phone" },
  {
    key: "type",
    label: "Types",
    type: "multiselect",
    options: DECORATOR_TYPE_OPTIONS,
    help: "Select all that apply — a card can belong to more than one type.",
  },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "contactUrl", label: "Contact URL (Instagram/Facebook/etc.)", type: "url" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "description", label: "Description", type: "textarea" },
  ...SOCIAL_FIELDS,
];

// Type filter chips shown above the listings. "Performing Artists" is not a
// card type stored on this page — clicking it navigates to the dedicated
// Performing Artists page instead of filtering in place. "DJs" navigates to
// the standalone DJs page on the live site (external URL).
const EVENT_MANAGEMENT_TYPES: { name: string; href?: string }[] = [
  { name: "Decorators" },
  { name: "Entertainment" },
  { name: "DJs", href: "https://dallasdesiscoop.com/djs" },
  { name: "Rentals" },
  { name: "Valet Services" },
  { name: "Helpers" },
  { name: "Performing Artists", href: "/performing-artists" },
  { name: "Emcees", href: "/media" },
];

export interface Decorator {
  name: string;
  city: string;
  address: string;
  website: string;
  phone: string;
  image: string;
  mapsLink?: string;
  contactUrl?: string;
  description?: string;
  // A card can belong to multiple types (e.g. both "Decorators" and
  // "Rentals"). New rows store a string array; older rows saved before
  // multiselect existed may still hold a plain string — getTypes() below
  // normalizes both shapes for filtering/matching.
  type?: string | string[];
}

export function getTypes(type?: string | string[]): string[] {
  if (Array.isArray(type)) return type.map((t) => String(t).trim()).filter(Boolean);
  // The platform datatable declares `type` as a `string` column, so a saved
  // multiselect comes back comma-joined ("Decorators,Rentals"). Wrapping that
  // whole string in an array would match none of the filter chips — split it.
  if (typeof type === "string") return type.split(",").map((t) => t.trim()).filter(Boolean);
  return [];
}

export const DECORATORS: Decorator[] = [
  {
    // Moved here from Home Services per admin request.
    name: "Erika Ramos",
    city: "",
    address: "Address coming soon",
    website: "",
    phone: "(469) 684-2790",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783544369554-u0qfsn.png",
    description: "Event assistance, bartending, Cleaning",
    type: "Helpers",
  },
  {
    name: "Sandy",
    city: "",
    address: "Address coming soon",
    website: "",
    phone: "(469) 831-9964",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1787522343412-z77w0s.png",
    description: "Event assistance, bartending, Cleaning",
    type: "Helpers",
  },
  {
    name: "Gabby",
    city: "",
    address: "Address coming soon",
    website: "",
    phone: "(214) 863-8783",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=400&fit=crop",
    description: "Event assistance, bartending, Cleaning",
    type: "Helpers",
  },
  {
    // Moved here from the Marketing/Production page (EventOrganizer) per admin request.
    name: "AJ Events & Decors",
    city: "Dallas, TX",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop",
    description: "From contemporary designs to traditional decor",
    contactUrl: "https://www.instagram.com/aj_eventsanddecors/",
    type: "Decorators",
  },
  {
    name: "AnnuzDecor",
    city: "Dallas",
    address: "Address coming soon",
    website: "https://www.facebook.com/AnnuzDecor",
    phone: "Coming soon",
    contactUrl: "https://www.facebook.com/AnnuzDecor",
    image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/4142edf214cf.jpeg",
    type: "Decorators",
  },
  {
    name: "Courtesy Valet Experts CVE",
    city: "Allen",
    address: "709 Furman Ct, Allen, TX 75013",
    website: "",
    phone: "(214) 434-2327",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1785537226131-2prgat.jpg",
    mapsLink: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("709 Furman Ct, Allen, TX 75013"),
    type: "Valet Services",
  },
  {
    name: "DQ_Decors",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784711301588-09ne87.png",
    contactUrl: "https://www.instagram.com/dq_decors/",
    type: "Decorators",
  },
  {
    name: "JMstudiocreativo",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784572301279-cwrtx.png",
    description: "Luxury Kids Party Design",
    contactUrl: "https://www.instagram.com/jmstudiocreativoo/",
    type: "Decorators",
  },
  {
    name: "LotusDecor",
    city: "Carrollton",
    address: "Address coming soon",
    website: "https://www.instagram.com/lotus.decor.events/",
    phone: "Coming soon",
    contactUrl: "https://www.instagram.com/lotus.decor.events/",
    image: "https://images.unsplash.com/photo-1510076857177-7470076d4098?w=600&h=400&fit=crop",
    type: "Decorators",
  },
  {
    name: "Maanarasi Events",
    city: "Frisco",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1785528644437-0ha3ct.jpg",
    contactUrl: "https://www.instagram.com/maanarisevents/",
    type: "Decorators",
  },
  {
    name: "signatureeventsbyshanti",
    city: "Irving",
    address: "Address coming soon",
    website: "#",
    phone: "Coming soon",
    contactUrl: "https://www.instagram.com/signatureeventsbyshanti/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1785528851893-ne65za.png",
    type: "Decorators",
  },
  {
    name: "Sona Events For You",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784285866800-l7jjtp.png",
    contactUrl: "https://www.instagram.com/sonaeventsforyou/",
    type: "Decorators",
  },
  {
    name: "Style Cracker Events",
    city: "Plano",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1785528643366-cggyns.jpg",
    contactUrl: "https://www.facebook.com/stylecrackerevent",
    type: "Decorators",
  },
  {
    name: "Suthradaar | Event Planning ⸱ Performance Choreography",
    city: "Dallas",
    address: "Dallas, TX",
    website: "",
    phone: "Coming soon",
    contactUrl: "https://www.instagram.com/suthradhaar/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783614660991-0m637p.png",
    type: "Decorators",
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop";

// Some cards had their website/facebook link intentionally cleared per admin
// request. useManagedCollection merges code defaults with a PERSISTED admin
// override row ({...defaults, ...override}) — the override always wins. If an
// override row from before this cleanup still has the old link saved, editing
// the source defaults alone can never remove it; the stale value keeps
// winning the merge on every reload. Force these fields blank at render time,
// after the merge, so no leftover (or future accidental) override can ever
// resurface the icon again — regardless of what's stored underneath.
const FORCE_BLANK_FIELDS: Record<string, string[]> = {
  "lotusdecor": ["website"],
  "annuzdecor": ["website"],
  "maanarasi-events": ["facebook", "website"],
};

// Same class of bug as FORCE_BLANK_FIELDS above, but for images: a stale
// admin-override row saved before these photos were provided can still hold
// an old image and win the merge inside useManagedCollection (overrides >
// defaults), so editing DECORATORS above alone can't guarantee the new photo
// actually shows. Force these two cards to always render the current code
// default image, regardless of any persisted override underneath.
const FORCE_DEFAULT_IMAGE = new Set(["maanarasi events", "style cracker events", "signatureeventsbyshanti"]);
const DEFAULT_IMAGE_BY_NAME: Record<string, string> = Object.fromEntries(
  DECORATORS.map((d) => [d.name.toLowerCase(), d.image])
);

// This card's photo is a business-card/flyer graphic — cropping it to fill
// the frame (object-cover) cuts off the logo/text. Show the whole image
// uncropped on a neutral backdrop instead.
const OBJECT_CONTAIN_NAMES = new Set(["courtesy valet experts cve"]);

export function sanitizeDecorator(d: Decorator): Decorator {
  const key = slugify(d.name || "");
  const blanks = FORCE_BLANK_FIELDS[key];
  if (!blanks || !blanks.length) return d;
  const clone = { ...d } as any;
  for (const f of blanks) clone[f] = "";
  return clone;
}

// Resolve the "View Location" destination, or "" when the card has no location
// to point at. Previously this ignored `address` completely: it used `mapsLink`
// if present and otherwise fabricated a search for `${name}, Dallas TX`. Two
// consequences, both wrong — a card WITH a precise saved address still linked to
// a generic name search (Events by UB → "Events by UB, Dallas TX" instead of
// 2217 Stella Lane, Northlake), and because the fallback always produced a URL,
// the button could never be suppressed by the absence of a location; only the
// separate hasRealAddress check kept it off, so any blank-ish value that slipped
// past that check still rendered a button leading nowhere useful.
const PLACEHOLDER_ADDRESSES = new Set(["address coming soon", "coming soon", "n/a", "na", "-", "tbd"]);

export function mapsUrl(d: Decorator) {
  const explicit = (d.mapsLink || (d as any).mapsUrl || "").trim();
  if (explicit) return explicit;
  const addr = (d.address || "").trim();
  if (!addr || PLACEHOLDER_ADDRESSES.has(addr.toLowerCase())) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}

// Resolve the image a card (or its detail page) should render, applying the
// FORCE_DEFAULT_IMAGE override so a stale admin row can never resurface an
// old photo. Shared by DecoratorCard and EventManagementDetailPage.
export function decoratorImage(d: Decorator): string {
  const key = (d.name || "").toLowerCase();
  if (FORCE_DEFAULT_IMAGE.has(key)) {
    return DEFAULT_IMAGE_BY_NAME[key] || d.image || FALLBACK_IMAGE;
  }
  return d.image || FALLBACK_IMAGE;
}

export function DecoratorCard({ decorator, index }: { decorator: Decorator; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className={`w-full h-[110px] relative overflow-hidden ${OBJECT_CONTAIN_NAMES.has((decorator.name || "").toLowerCase()) ? "bg-gray-50" : ""}`}>
        <img
          src={
            FORCE_DEFAULT_IMAGE.has((decorator.name || "").toLowerCase())
              ? DEFAULT_IMAGE_BY_NAME[(decorator.name || "").toLowerCase()] || decorator.image
              : decorator.image
          }
          alt={decorator.name}
          className={`w-full h-full ${OBJECT_CONTAIN_NAMES.has((decorator.name || "").toLowerCase()) ? "object-contain" : "object-cover"}`}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-2.5 pb-1 flex-1">
        <Link
          to={`/event-management/${slugify(decorator.name || "")}`}
          className="block"
          aria-label={`${decorator.name} details`}
        >
        <h3 className="text-[13px] text-gray-900 leading-tight mb-0.5">
          {(() => {
            // "Lucky Charms Decor, Wedding Decor by Prathyusha" is admin-added
            // (lives in the persisted decorators data store, not the DECORATORS
            // array above) and its name string always contains a comma. Per
            // admin request the part before the comma stays bold and the
            // tagline after it renders regular weight. This is scoped by
            // matching the actual name text so it works no matter where the
            // row comes from (code default or admin override).
            const name = decorator.name || "";
            const commaIdx = name.indexOf(",");
            const isLuckyCharms = name.toLowerCase().startsWith("lucky charms decor");
            if (isLuckyCharms && commaIdx !== -1) {
              return (
                <>
                  <span className="font-bold">{name.slice(0, commaIdx)}</span>
                  <span className="font-normal">{name.slice(commaIdx)}</span>
                </>
              );
            }
            return <span className="font-bold">{name}</span>;
          })()}
        </h3>
        </Link>
        {decorator.address &&
          decorator.address.trim() !== "" &&
          decorator.address !== "Address coming soon" && (
          <div className="flex items-start gap-1 mb-0.5">
            <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
            <span className="text-[10px] text-gray-500 font-medium leading-snug">
              {decorator.address}
            </span>
          </div>
        )}
        {decorator.description && (
          <p className="text-[10px] text-gray-500 font-normal leading-snug mt-0.5">
            {decorator.description}
          </p>
        )}
      </div>
      <div className="px-2.5 pb-2.5 pt-1">
        {(() => {
          // The big button always points at contactUrl (per admin request) —
          // it is NOT derived from the dedicated website/instagram/facebook
          // fields, and it is NOT a stand-in for those fields. Whether it
          // renders as a Facebook or Instagram button is just cosmetic,
          // based on what domain contactUrl happens to be.
          const linkUrl = decorator.contactUrl && decorator.contactUrl !== "#" ? decorator.contactUrl : null;
          const isFacebookContact = !!linkUrl && linkUrl.includes("facebook.com");

          // Any card with a real phone number (admin-entered, not the
          // "Coming soon" placeholder) always gets a green Call button.
          const rawPhone = (decorator.phone || "").trim();
          const hasPhone = !!rawPhone && rawPhone.toLowerCase() !== "coming soon";
          const callButton = hasPhone ? (
            <a
              href={`tel:${rawPhone.replace(/[^\d+]/g, "")}`}
              className="w-full flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
            >
              <Phone className="w-2.5 h-2.5" /> Call
            </a>
          ) : null;

          // Facebook contact renders as its own row (kept separate since it
          // can carry a second Instagram button alongside it for Style
          // Cracker Events specifically). Sized to match the smaller Call /
          // View Location buttons instead of the old oversized style.
          const facebookRow =
            linkUrl && isFacebookContact ? (
              <div className="flex gap-1.5 w-full">
                <a
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
                  style={{ backgroundColor: "#1877F2" }}
                >
                  <Facebook className="w-2.5 h-2.5" /> Facebook
                </a>
                {decorator.name === "Style Cracker Events" && (
                  <a
                    href="https://www.instagram.com/mamta.chhabra25"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                    }}
                  >
                    <Instagram className="w-2.5 h-2.5" /> Instagram
                  </a>
                )}
              </div>
            ) : null;

          // Single-link Instagram button, sized to match Call / View Location
          // so it sits in the same small row instead of looming full-width
          // above them. No contactUrl entered → render nothing (no disabled
          // placeholder).
          const instagramButton =
            linkUrl && !isFacebookContact ? (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-1 text-white text-[10px] font-semibold py-1.5 rounded-md transition-opacity duration-200 hover:opacity-90"
                style={{
                  background:
                    "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                }}
              >
                <Instagram className="w-2.5 h-2.5" /> Instagram
              </a>
            ) : null;

          // The small icon row must reflect ONLY the dedicated per-platform
          // fields the admin actually filled in (item.facebook, item.instagram,
          // etc. via SOCIAL_FIELDS) — never derived from contactUrl/website.
          // Previously this row derived facebook/instagram from contactUrl,
          // which showed a Facebook icon even when the Facebook URL field was
          // left empty. Pass the raw decorator through so SocialLinks' own
          // "only render if the field has a value" logic is the single
          // source of truth.
          // Single source of truth: if mapsUrl() cannot resolve a real
          // destination (explicit link or a genuine address), there is no
          // button at all — a card saved without a location must not show one.
          const locationHref = mapsUrl(decorator);
          // All View Location buttons on Event Management cards are blue.
          const viewLocationButton = locationHref ? (
            <a
              href={locationHref}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-1 text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <ExternalLink className="w-2.5 h-2.5" /> View Location
            </a>
          ) : null;

          // Facebook (which may itself carry a second Instagram button for
          // Style Cracker Events) always gets its own line. Every other
          // small action — single Instagram, Call, View Location — sits
          // together in one compact row instead of stacking full-width.
          const inlineButtons = [
            instagramButton && <div key="ig" className="flex-1">{instagramButton}</div>,
            callButton && <div key="call" className="flex-1">{callButton}</div>,
            viewLocationButton && <div key="loc" className="flex-1">{viewLocationButton}</div>,
          ].filter(Boolean);

          return (
            <>
              <div className="mb-1.5">
                <SocialLinks item={decorator} px={20} />
              </div>
              {facebookRow}
              {inlineButtons.length > 0 && (
                <div className={`${facebookRow ? "mt-1.5" : ""} flex items-center gap-1.5`}>
                  {inlineButtons}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </motion.div>
  );
}

// "AJ Entertainments" moved to the Marketing/Production page (EventOrganizer,
// route /event-production) — keep it out of this page's list even if a stale
// admin-added row for it still exists in the decorators data store.
const MOVED_OUT_NAMES = new Set(["aj entertainments"]);

export default function Decorators() {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const navigate = useNavigate();
  const mc = useManagedCollection<any>("decorators", DECORATORS, (d, i) => slugify(d.name || String(i)));

  const filtered = mc.items
    .filter(
      (d) =>
        !MOVED_OUT_NAMES.has((d.name || "").toLowerCase()) &&
        (query.trim() === "" ||
          d.name.toLowerCase().includes(query.trim().toLowerCase())) &&
        (!selectedType ||
          getTypes(d.type).some((t) => t.toLowerCase() === selectedType.toLowerCase()))
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        placeholder="Search event planning…"
        query={query}
        setQuery={setQuery}
        hideLocation
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/f5320a28c10f.jpeg"
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: decorators */}
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Event Planning in Dallas–DFW
            </h2>
            <p className="text-[13px] text-gray-500 mb-4">
              Make every moment picture-perfect with beautifully curated event décor
            </p>

            {/* Type filter chips */}
            <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
              {EVENT_MANAGEMENT_TYPES.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => {
                    if (t.href) {
                      // External links (absolute URLs) must leave the SPA via
                      // a full navigation — react-router's navigate() only
                      // handles in-app routes and would mis-route an absolute
                      // URL like the DJs page link.
                      if (/^https?:\/\//i.test(t.href)) {
                        window.location.href = t.href;
                      } else {
                        navigate(t.href);
                      }
                      return;
                    }
                    setSelectedType(selectedType === t.name ? null : t.name);
                  }}
                  className={
                    "shrink-0 px-3 py-1.5 rounded-full text-[11px] sm:text-[12px] font-medium border transition-all duration-200 " +
                    (selectedType === t.name
                      ? "bg-orange-50 text-orange-700 border-orange-300"
                      : "bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:bg-orange-50/50")
                  }
                >
                  {t.name}
                </button>
              ))}
              {selectedType && (
                <button
                  type="button"
                  onClick={() => setSelectedType(null)}
                  className="shrink-0 px-3 py-1.5 rounded-full text-[11px] sm:text-[12px] font-medium border border-dashed border-gray-300 text-gray-500 hover:text-orange-700 hover:border-orange-300 transition-colors duration-200"
                >
                  ✕ Clear
                </button>
              )}
            </div>

            <AdminAddBar mc={mc} fields={DECORATOR_FIELDS} label="Add Event Management" />

            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
                {filtered.map((d, i) => (
                  <AdminItem key={d._key} mc={mc} item={d} fields={DECORATOR_FIELDS} title="Decorator">
                    <DecoratorCard decorator={sanitizeDecorator(d as any)} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No decorators match your filters.
              </div>
            )}
          </div>

          {/* Instagram widget removed — only on home page */}
        </div>
      </section>
    </div>
  );
}
